import { DatePipe } from "@angular/common";
import { Component, OnInit, ViewChild, Injector } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { DialogService } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import { PermissionConstants } from "src/app/core/constants/permission-constants.constant";
import { DateTableColumn } from "src/app/core/models/date-table-column";
import { isNullOrEmpty } from "src/app/core/utils/common-functions";
import { DataViewComponent } from "src/app/shared/components/data-view/data-view.component";
import { SessionBaseComponent } from "src/app/shared/components/session.base.component";
import { Table } from "typeorm";
import { HelpCenterResp } from "./model/HelpCenterResp";
import { HelpCenterSearchParam } from "./model/HelpCenterSearchParam";
import { HelpCenterTableRow } from "./model/HelpCenterTableRow";
import { HelpCenterService } from "../../service/help-center.service";
import { HelpCenterFileEditComponent } from "./help-center-file-edit/help-center-file-edit.component";

@Component({
  selector: 'help-center',
  styleUrls: ['./help-center-setting.component.scss'],
  templateUrl: './help-center-setting.component.html',
})
export class HelpCenterSettingComponent extends SessionBaseComponent implements OnInit {

  userArticleEditPermission: boolean = false;
  iTArticleEditPermission: boolean = false;
  userGuideDocEditPermission: boolean = false;
  userDocEditPermission: boolean = false;

  searchForm: FormGroup;
  isLoading: boolean;
  viewMode: string = 'ALL';
  helpCenterDropdown: any[];

  tableValue: HelpCenterTableRow[];
  tableColumns: DateTableColumn[];
  singleGuideValue?: HelpCenterResp;
  singleUserGuideExist: boolean = false;
  singleUserFileExist: boolean = false;

  readonly userArticleEditPermissionCode: PermissionConstants = PermissionConstants.HelpUserArticleEdit;
  readonly iTArticleEditPermissionCode: PermissionConstants = PermissionConstants.HelpITArticleEdit;
  readonly userGuideDocEditPermissionCode: PermissionConstants = PermissionConstants.UserGuideDocEdit;
  readonly userDocEditPermissionCode: PermissionConstants = PermissionConstants.HelpUserDocEdit;

  @ViewChild('ViewTable') viewTable!: Table;
  @ViewChild('LaasDataView') laasDataView!: DataViewComponent;

  lang: string = this.laasSetting.language;
  private datePipe: DatePipe;

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private helpCenterService: HelpCenterService,
    private dialogService: DialogService,
  ) {
    super(injector);

    // 初始化 DatePipe
    this.datePipe = new DatePipe('en-US');

    this.isLoading = false;
    this.helpCenterDropdown = [];

    this.searchForm = this.formBuilder.group({
      permissionCode: [null, Validators.required],
    });
    this.tableValue = [];

    this.userArticleEditPermission = this.permissionCodes.includes(this.userArticleEditPermissionCode);
    this.iTArticleEditPermission = this.permissionCodes.includes(this.iTArticleEditPermissionCode);
    this.userGuideDocEditPermission = this.permissionCodes.includes(this.userGuideDocEditPermissionCode);
    this.userDocEditPermission = this.permissionCodes.includes(this.userDocEditPermissionCode);
    console.log(this.userArticleEditPermission,this.iTArticleEditPermission,this.userGuideDocEditPermission,this.userDocEditPermission);
    this.tableColumns = [
      {
        field: 'guideHeader',
        header: 'HelpCenter.Setting.Results.FunctionName',
        sort: true
      },
      {
        field: 'userArticle',
        header: 'HelpCenter.Setting.Results.UserArticle'
      },
      {
        field: 'itArticle',
        header: 'HelpCenter.Setting.Results.ITArticle'
      },
      {
        field: 'files',
        header: 'HelpCenter.Setting.Results.Files'
      },
    ];

     const routeData = this.routeStateService.getCurrent()?.data;
    if (!isNullOrEmpty(routeData)) {
      this.searchForm.controls.permissionCode.setValue(routeData.queryParams.permissionCode);
      this.lang = routeData.queryParams.lang;
      this.searchBtnClick();
    }
  }
  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    const rawDropdown: any[] = await this.helpCenterService.getHelpCenterDropdown(this.lang).toPromise() || [];
    this.helpCenterDropdown = [
      { label: 'ALL', value: 'ALL' },
      ...rawDropdown.map(item => ({
        label: item.guideHeader,
        value: item.permissionCode
      }))
    ];

    this.userArticleEditPermission = this.permissionCodes.includes(this.userArticleEditPermissionCode);
    this.iTArticleEditPermission = this.permissionCodes.includes(this.iTArticleEditPermissionCode);
    this.userGuideDocEditPermission = this.permissionCodes.includes(this.userGuideDocEditPermissionCode);
    this.userDocEditPermission = this.permissionCodes.includes(this.userDocEditPermissionCode);
    console.log(this.userArticleEditPermission,this.iTArticleEditPermission,this.userGuideDocEditPermission,this.userDocEditPermission);
  }

  searchBtnClick() {
    // 標記表單為已觸碰，顯示驗證錯誤
    this.searchForm.markAllAsTouched();

    // 如果表單無效，不執行查詢
    if (this.searchForm.invalid) {
      this.toastService.warn(
        this.translateService.instant('Common.Toast.CheckRequiredFields')
      );
      return;
    }

    this.isLoading = true;
    const permissionCode = this.searchForm.value['permissionCode'];

    // 判斷是否為 'ALL' 或空值
    if (isNullOrEmpty(permissionCode) || permissionCode === 'ALL') {
      this.viewMode = "ALL";

      // 如果後端已經合併好，直接使用
      this.helpCenterService.getAllHelpCenterList().subscribe({
        next: (result: any[]) => {
          // 檢查後端是否已經合併（有 userArticle 和 itArticle 欄位）
          if (result.length > 0 && result[0].userArticle !== undefined) {
            // 後端已合併，直接使用
            this.tableValue = result;
          } else {
            // 後端未合併，前端處理
            this.tableValue = this.mergeHelpCenterData(result);
          }
        },
        error: (error) => {
          console.error('查詢失敗:', error);
          this.toastService.error(error);
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    } else {
      this.viewMode = "SINGLE";

      const searchRequest: HelpCenterSearchParam = {
        permissionCode: this.searchForm.get('permissionCode')?.value ?? '',
      };

      this.helpCenterService.getHelpCenterByPermissionAndLang(searchRequest).subscribe({
        next: (result) => {
          this.singleGuideValue = result;
          this.logSingleGuideContentStatus(result);

          // 查詢 UserGuide / UserFile 是否已上傳
          this.helpCenterService.existHelpCenter(searchRequest.permissionCode, this.lang).subscribe({
            next: (existResp) => {
              this.singleUserGuideExist = existResp.userGuideExist;
              this.singleUserFileExist = existResp.userFileExist;
            }
          });
        },
        error: (error) => {
          console.error('查詢失敗:', error);
          this.toastService.error(error);
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * 前端合併資料（備用方案）
   * 如果後端沒有合併，前端自己合併
   */
  private mergeHelpCenterData(rawData: HelpCenterResp[]): HelpCenterTableRow[] {
    const grouped: { [key: string]: HelpCenterTableRow } = {};

    rawData.forEach(item => {
      const key = `${item.permissionCode}_${item.lang || 'default'}`;

      // 初始化
      if (!grouped[key]) {
        grouped[key] = {
          permissionCode: item.permissionCode,
          guideHeader: item.guideHeader,
          lang: item.lang || 'zh-TW',
          userArticle: null,
          itArticle: null
        };
      }

      // 判斷是否有內容
      const hasUserReleasedContent = this.hasMeaningfulContent(item.releasedGuideContent);
      const hasUserDraftContent = this.hasMeaningfulContent(item.draftGuideContent);
      const hasITReleasedContent = this.hasMeaningfulContent(item.releasedGuideContentIT);
      const hasITDraftContent = this.hasMeaningfulContent(item.draftGuideContentIT);
      const hasUserContent = hasUserReleasedContent || hasUserDraftContent;
      const hasITContent = hasITReleasedContent || hasITDraftContent;

      // User 文章
      if (hasUserContent) {
        grouped[key].userArticle = {
          hasContent: true,
          isPublished: hasUserReleasedContent,
          updateUser: item.updateUser,
          updateDt: this.formatDate(item.updateDt)
        };
      }

      // IT 文章
      if (hasITContent) {
        grouped[key].itArticle = {
          hasContent: true,
          isPublished: hasITReleasedContent,
          updateUser: item.updateUser,
          updateDt: this.formatDate(item.updateDt)
        };
      }

      // 如果兩個都沒有，至少保留空狀態
      if (!hasUserContent && !grouped[key].userArticle) {
        grouped[key].userArticle = {
          hasContent: false,
          isPublished: false
        };
      }

      if (!hasITContent && !grouped[key].itArticle) {
        grouped[key].itArticle = {
          hasContent: false,
          isPublished: false
        };
      }
    });

    return Object.values(grouped);
  }

  hasMeaningfulContent(content: string | null | undefined): boolean {
    if (isNullOrEmpty(content)) {
      return false;
    }

    const safeContent = content ?? '';

    const plainText = safeContent
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6]|tr|td|th)>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, '')
      .trim();

    return plainText.length > 0;
  }

  hasSingleUserContent(): boolean {
    return this.hasMeaningfulContent(this.singleGuideValue?.releasedGuideContent)
      || this.hasMeaningfulContent(this.singleGuideValue?.draftGuideContent);
  }

  hasSingleUserPublishedContent(): boolean {
    return this.hasMeaningfulContent(this.singleGuideValue?.releasedGuideContent);
  }

  hasSingleITContent(): boolean {
    return this.hasMeaningfulContent(this.singleGuideValue?.releasedGuideContentIT)
      || this.hasMeaningfulContent(this.singleGuideValue?.draftGuideContentIT);
  }

  hasSingleITPublishedContent(): boolean {
    return this.hasMeaningfulContent(this.singleGuideValue?.releasedGuideContentIT);
  }

  private logSingleGuideContentStatus(result: HelpCenterResp): void {
    const hasUserReleasedContent = this.hasMeaningfulContent(result.releasedGuideContent);
    const hasUserDraftContent = this.hasMeaningfulContent(result.draftGuideContent);
    const hasITReleasedContent = this.hasMeaningfulContent(result.releasedGuideContentIT);
    const hasITDraftContent = this.hasMeaningfulContent(result.draftGuideContentIT);

    console.log('[HelpCenter] SINGLE content status', {
      permissionCode: result.permissionCode,
      guideHeader: result.guideHeader,
      user: {
        releasedGuideContent: result.releasedGuideContent,
        draftGuideContent: result.draftGuideContent,
        hasReleasedContent: hasUserReleasedContent,
        hasDraftContent: hasUserDraftContent,
        showTag: hasUserReleasedContent || hasUserDraftContent,
        tagStatus: hasUserReleasedContent ? 'published' : (hasUserDraftContent ? 'unpublished' : 'none')
      },
      it: {
        releasedGuideContentIT: result.releasedGuideContentIT,
        draftGuideContentIT: result.draftGuideContentIT,
        hasReleasedContent: hasITReleasedContent,
        hasDraftContent: hasITDraftContent,
        showTag: hasITReleasedContent || hasITDraftContent,
        tagStatus: hasITReleasedContent ? 'published' : (hasITDraftContent ? 'unpublished' : 'none')
      }
    });
  }

  /**
   * 格式化日期為字串
   */
  private formatDate(date: Date | string | null | undefined): string | undefined {
    if (!date) {
      return undefined;
    }

    // 如果已經是字串，直接回傳
    if (typeof date === 'string') {
      return date;
    }

    // 使用 DatePipe 轉換
    return this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss') || undefined;
  }

  resetBtnClick() {
    this.searchForm.reset({
      permissionCode: '',
    });
  }

  /**
   * 檢視/建立 User 文章（移除 hasContent 檢查，允許建立新文章）
   */
  openUserArticle(row: HelpCenterTableRow): void {
    console.log('開啟 User 文章:', row);

    // 🎯 移除檢查，即使沒有內容也能開啟
    // if (!row.userArticle?.hasContent) {
    //   this.toastService.info(
    //     this.translateService.instant('HelpCenter.Message.NoUserArticle')
    //   );
    //   return;
    // }

    this.openArticle(row.guideHeader, 'USER', row.permissionCode);
  }

  /**
   *  檢視/建立 IT 文章（移除 hasContent 檢查，允許建立新文章）
   */
  openITArticle(row: HelpCenterTableRow): void {
    this.openArticle(row.guideHeader, 'IT', row.permissionCode);
  }

  /**
   * 單一畫面卡片：直接帶 permissionCode 開啟
   */
  async openArticleFromCard(type: 'USER' | 'IT'): Promise<void> {
    if (!this.singleGuideValue) {
      return;
    }

    this.openArticle(
      this.singleGuideValue.guideHeader,
      type,
      this.singleGuideValue.permissionCode
    );
  }

  /**
   * 核心：打開檢視/編輯頁面
   */
  private async openArticle(
    guideHeader: string,
    type: 'USER' | 'IT',
    permissionCode: string
  ): Promise<void> {

    // 1) 標題

    const typeLabel = await firstValueFrom(
      this.translateService.get(
        type === 'IT'
          ? 'HelpCenter.Setting.Results.ITArticle'
          : 'HelpCenter.Setting.Results.UserArticle'
      )
    );
    const headerText = `${typeLabel} - ${guideHeader}`;

    // 2) 是否可編輯
    const canEdit = type === 'IT' ? this.iTArticleEditPermission : this.userArticleEditPermission;

    console.log('導航參數:', {
      header: headerText,
      isIT: type === 'IT',
      permissionCode,
      canEdit
    });

    // 3) 跳轉編輯頁面
    this.routeStateService.navigateTo(
      '/Setting/HelpCenterEdit',
      {
        header: headerText,
        width: '80vw',
        dismissableMask: true,
        closable: true,
        data: {
          isIT: type === 'IT',
          permissionCode: permissionCode,
          canEdit
        }
      },
      true
    );
  }

  /**
   * 開啟檔案管理 Dialog
   */
  async openFileDialog(
    row: HelpCenterTableRow | HelpCenterResp | undefined,
    fileType: string
  ): Promise<void> {
    if (!row) {
      console.warn('row 是空的，無法開啟檔案 Dialog');
      return;
    }

    const headerText = `${fileType} - ${row.guideHeader}`;
    const editPermision = fileType === 'UserGuide' ? this.userGuideDocEditPermission : this.userDocEditPermission;

    this.dialogService.open(HelpCenterFileEditComponent, {
      header: headerText,
      width: '80vw',
      height: '80vh',
      data: {
        permissionCode: row.permissionCode,
        editPermision: editPermision,
        fileType: fileType
      },
      closable: true,
    });
  }
}
